import { validateNewFormFileName } from "@/domain/process-model/form-files";

import { defaultReadTextDeps, type ReadTextDeps } from "./read-file-text";
import {
  type FormSchemaIssue,
  validateFormSchema,
} from "./validate-form-schema";

/** 選んだファイルがフォーム定義として使えない理由。画面はこれを見て文言を出す。 */
export type FormFileIssue =
  /** .bpmn を選んだ。図は「取り込み」から差し替える。 */
  | { readonly kind: "bpmn" }
  /** 拡張子が .json ではない。 */
  | { readonly kind: "not-json" }
  /** 名前が規約（`*-schema.json` / `*-uischema.json`）に合わない、または使えない文字がある。 */
  | { readonly kind: "name"; readonly reason: "invalid" | "convention" }
  /** ファイルを読めなかった（権限・破損など）。 */
  | { readonly kind: "unreadable" }
  /** 中身が保存前の検証（validateFormSchema）を通らない。 */
  | {
      readonly kind: "contents";
      readonly reason: FormSchemaIssue;
      readonly detail: string | null;
    }
  /** 同じ名前のファイルを 2 回選んだ。 */
  | { readonly kind: "duplicate" };

/** 取り込みの確認欄の 1 行。 */
export type FormFileRow = {
  readonly fileName: string;
  readonly contents: string;
  /** モデルに同じ名前があれば上書き（PUT）、無ければ新規（POST）。 */
  readonly action: "create" | "overwrite";
  readonly issue: FormFileIssue | null;
};

/**
 * 選んだ（またはドロップした）ファイルを 1 つ読み、フォーム定義として使えるか判定する。
 *
 * 名前は選んだファイルのまま保存する（BPMN が `formJsonSchemaFilename` で名前を指す
 * ので、手で付け直すと 1 文字の違いで孤児フォームになる）。だから名前の規約は
 * 「フォームを追加」と同じ `validateNewFormFileName` で見る。ただし「モデルに同じ名前が
 * ある」は弾かず上書きにする（AI の出力で既存のフォームを更新する使い方のため）。
 *
 * 中身は `*-schema.json` / `*-uischema.json` のどちらも `validateFormSchema` に通す
 * （フォーム定義の編集と同じ検証。properties が無くても通るので uischema も通る。
 * 正確な規則は validate-form-schema.ts が持つ）。
 */
export async function readFormFile(
  file: File,
  existing: readonly string[],
  deps: ReadTextDeps = defaultReadTextDeps,
): Promise<FormFileRow> {
  const base = {
    fileName: file.name,
    action: existing.includes(file.name) ? "overwrite" : "create",
  } as const;

  const nameIssue = checkName(file.name);
  if (nameIssue !== null) return { ...base, contents: "", issue: nameIssue };

  let contents: string;
  try {
    contents = await deps.readText(file);
  } catch {
    return { ...base, contents: "", issue: { kind: "unreadable" } };
  }

  const validation = validateFormSchema(contents);
  if (!validation.ok) {
    return {
      ...base,
      contents,
      issue: {
        kind: "contents",
        reason: validation.reason,
        detail: validation.detail,
      },
    };
  }

  return { ...base, contents, issue: null };
}

/** 拡張子と名前の規約。拡張子はここで見る（file-drop-zone はドロップを絞らない）。 */
function checkName(fileName: string): FormFileIssue | null {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".bpmn")) return { kind: "bpmn" };
  if (!lower.endsWith(".json")) return { kind: "not-json" };

  // 既存との重複は上書きとして扱うので、ここでは名前の形だけ見る
  const check = validateNewFormFileName(fileName, []);
  if (check.ok) return null;
  return {
    kind: "name",
    reason: check.reason === "convention" ? "convention" : "invalid",
  };
}

/**
 * 同じ名前を 2 回選んでいたら、後の行に理由を付ける。
 *
 * 行を外した後に計算し直すので、先の行を外せば後の行は使えるようになる。
 * 別の理由が既にある行は、より具体的なその理由を残す。
 */
export function markDuplicateSelections(
  rows: readonly FormFileRow[],
): readonly FormFileRow[] {
  const seen = new Set<string>();
  return rows.map((row) => {
    const isDuplicate = seen.has(row.fileName);
    seen.add(row.fileName);
    return isDuplicate && row.issue === null
      ? { ...row, issue: { kind: "duplicate" } }
      : row;
  });
}

/** 使えると分かった行。送れるのはこの型だけ。 */
export type ReadyFormFileRow = FormFileRow & { readonly issue: null };

/**
 * 行が無いか、使えない行が 1 つでも残っている間は確定させない（黙って一部だけ
 * 入れない）。通れば送れる型に絞り込む（拒否行の空の contents で上書きしない）。
 */
export function canImportFormFiles(
  rows: readonly FormFileRow[],
): rows is readonly ReadyFormFileRow[] {
  return rows.length > 0 && rows.every((row) => row.issue === null);
}
